import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useRef, type ReactNode } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { supabase } from "@/integrations/supabase/client";

const articleQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["news", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_articles")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });

const relatedQueryOptions = (slug: string, language: string | null) =>
  queryOptions({
    queryKey: ["news", "related", slug, language],
    queryFn: async () => {
      let query = supabase
        .from("news_articles")
        .select("id, slug, title, excerpt, cover_image_url, published_at")
        .eq("published", true)
        .neq("slug", slug)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(3);
      if (language) query = query.eq("language", language);
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });

export const Route = createFileRoute("/news_/$slug")({
  component: ArticlePage,
  loader: async ({ context, params }) => {
    const article = await context.queryClient.ensureQueryData(articleQueryOptions(params.slug));
    return { article };
  },
  head: ({ params, loaderData }) => {
    const a = loaderData?.article;
    const url = `https://comm42.ch/news/${params.slug}`;
    if (!a) {
      return {
        meta: [
          { title: "Article — Comm42 SA" },
          { name: "description", content: "Editorial article from Comm42 — strategy, investigation, legal protection and international logistics." },
          { property: "og:url", content: url },
          { property: "og:type", content: "article" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const rawExcerpt = (a.excerpt || a.content || "").replace(/[#*>_`\[\]!()]/g, "").replace(/\s+/g, " ").trim();
    const desc = rawExcerpt.length > 155 ? rawExcerpt.slice(0, 152).trimEnd() + "…" : rawExcerpt || "Editorial article from Comm42 SA.";
    const title = `${a.title} — Comm42 SA`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(a.cover_image_url ? [
          { property: "og:image", content: a.cover_image_url },
          { property: "og:image:alt", content: a.title },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:image", content: a.cover_image_url },
        ] : []),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: a.title.length > 110 ? a.title.slice(0, 107) + "…" : a.title,
            description: desc,
            image: a.cover_image_url ? [a.cover_image_url] : undefined,
            datePublished: a.published_at || undefined,
            dateModified: a.updated_at || a.published_at || undefined,
            inLanguage: a.language || "it",
            articleSection: "News",
            keywords: [
              "Comm42",
              "corporate investigation",
              "due diligence",
              "compliance",
              "Switzerland",
              "risk intelligence",
            ],
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            author: {
              "@type": "Organization",
              name: "Comm42 SA",
              url: "https://comm42.ch",
            },
            publisher: {
              "@type": "Organization",
              name: "Comm42 SA",
              url: "https://comm42.ch",
              logo: {
                "@type": "ImageObject",
                url: "https://comm42.ch/favicon.svg",
              },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://comm42.ch/" },
              { "@type": "ListItem", position: 2, name: "News", item: "https://comm42.ch/news" },
              { "@type": "ListItem", position: 3, name: a.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-6 py-40 text-center">
        <h1 className="font-serif text-4xl">404</h1>
        <p className="mt-4 text-muted-foreground">Article not found.</p>
        <Link to="/news" className="mt-6 inline-block text-primary">← Back to News</Link>
      </div>
    </PageShell>
  ),
});

function renderInlineMarkdown(text: string) {
  const nodes: ReactNode[] = [];
  const pattern = /(!?\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));

    if (match[1]?.startsWith("!")) {
      nodes.push(<img key={match.index} src={match[3]} alt={match[2]} loading="lazy" />);
    } else if (match[2] && match[3]) {
      nodes.push(
        <a key={match.index} href={match[3]} target="_blank" rel="noreferrer">
          {match[2]}
        </a>,
      );
    } else if (match[4]) {
      nodes.push(<strong key={match.index}>{match[4]}</strong>);
    } else if (match[5]) {
      nodes.push(<em key={match.index}>{match[5]}</em>);
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function renderMarkdown(content: string | null) {
  if (!content) return null;

  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const out: ReactNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length) {
      out.push(<p key={key++} className="my-5 whitespace-pre-wrap break-words leading-8 md:my-6 md:leading-8">{renderInlineMarkdown(paragraph.join("\n"))}</p>);
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      out.push(
        <ul key={key++} className="my-5 list-disc space-y-3 pl-6 leading-8 md:my-6">
          {list.map((item, i) => (
            <li key={i}>{renderInlineMarkdown(item)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    if (line.startsWith("### ")) {
      flushParagraph(); flushList();
      out.push(<h3 key={key++} className="mb-4 mt-9 break-words font-serif text-xl leading-snug md:mt-10 md:text-2xl">{renderInlineMarkdown(line.slice(4))}</h3>);
      continue;
    }
    if (line.startsWith("## ")) {
      flushParagraph(); flushList();
      out.push(<h2 key={key++} className="mb-5 mt-11 break-words font-serif text-2xl leading-snug md:mt-12 md:text-3xl">{renderInlineMarkdown(line.slice(3))}</h2>);
      continue;
    }
    if (line.startsWith("# ")) {
      flushParagraph(); flushList();
      out.push(<h1 key={key++} className="mb-5 mt-11 break-words font-serif text-3xl leading-snug md:mt-12 md:text-4xl">{renderInlineMarkdown(line.slice(2))}</h1>);
      continue;
    }

    const image = line.match(/^!\[([^\]]*)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)$/);
    if (image) {
      flushParagraph(); flushList();
      out.push(<img key={key++} src={image[2]} alt={image[1]} loading="lazy" className="my-8 w-full rounded-lg" />);
      continue;
    }

    if (line.startsWith("> ")) {
      flushParagraph(); flushList();
      out.push(
        <blockquote key={key++} className="my-8 break-words border-l-4 border-primary py-1 pl-5 italic leading-8 text-foreground/80">
          {renderInlineMarkdown(line.slice(2))}
        </blockquote>,
      );
      continue;
    }

    const item = line.match(/^[-*]\s+(.*)$/);
    if (item) {
      flushParagraph();
      list.push(item[1]);
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return out;
}

function RelatedCarousel({
  articles,
  lang,
}: {
  articles: {
    id: string;
    slug: string;
    title: string;
    excerpt: string | null;
    cover_image_url: string | null;
    published_at: string | null;
  }[];
  lang: "it" | "en";
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    const delta = card?.offsetWidth ? card.offsetWidth + 20 : 320;
    el.scrollBy({ left: direction === "left" ? -delta : delta, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center">
        <button
          type="button"
          onClick={() => scroll("left")}
          aria-label={lang === "it" ? "Scorri a sinistra" : "Scroll left"}
          className="pointer-events-auto -ml-3 flex h-9 w-9 items-center justify-center rounded-full border border-border/80 bg-background/90 text-foreground shadow-sm backdrop-blur transition hover:border-primary hover:text-primary"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center">
        <button
          type="button"
          onClick={() => scroll("right")}
          aria-label={lang === "it" ? "Scorri a destra" : "Scroll right"}
          className="pointer-events-auto -mr-3 flex h-9 w-9 items-center justify-center rounded-full border border-border/80 bg-background/90 text-foreground shadow-sm backdrop-blur transition hover:border-primary hover:text-primary"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex snap-x snap-mandatory gap-5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {articles.map((r) => (
          <Link
            key={r.id}
            to="/news/$slug"
            params={{ slug: r.slug }}
            className="group min-w-[16rem] w-[16rem] snap-start overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary sm:min-w-[18rem] sm:w-[18rem]"
          >
            {r.cover_image_url ? (
              <div className="aspect-video w-full overflow-hidden bg-muted">
                <img
                  src={r.cover_image_url}
                  alt={r.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ) : (
              <div className="aspect-video w-full bg-muted" />
            )}
            <div className="flex flex-col p-5">
              {r.published_at && (
                <time className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                  {new Date(r.published_at).toLocaleDateString(
                    lang === "it" ? "it-CH" : "en-GB",
                    { day: "numeric", month: "long", year: "numeric" },
                  )}
                </time>
              )}
              <h3 className="mt-2 font-serif text-lg leading-snug text-foreground transition-colors group-hover:text-primary">
                {r.title}
              </h3>
              {r.excerpt && (
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{r.excerpt}</p>
              )}
              <span className="mt-4 text-xs uppercase tracking-widest text-primary group-hover:underline">
                {lang === "it" ? "Leggi l'articolo" : "Read the article"} →
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ArticlePage() {
  const { slug } = Route.useParams();
  const { i18n, t } = useTranslation();
  const lang = (i18n.language || "en").startsWith("it") ? "it" : "en";

  const { data: article, isLoading, error } = useQuery(articleQueryOptions(slug));
  const { data: related } = useQuery({
    ...relatedQueryOptions(slug, article?.language ?? null),
    enabled: !!article,
  });

  if (isLoading) {
    return (
      <PageShell>
        <div className="mx-auto max-w-3xl px-6 py-40 text-center text-muted-foreground">
          {t("news.loading")}
        </div>
      </PageShell>
    );
  }
  if (error || !article) {
    return (
      <PageShell>
        <div className="mx-auto max-w-3xl px-6 py-40 text-center">
          <h1 className="font-serif text-4xl">404</h1>
          <Link to="/news" className="mt-6 inline-block text-primary">← Back</Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <article className="mx-auto max-w-3xl px-6 pb-20 pt-32">
        <Link to="/news" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-3 w-3" /> {t("news.back")}
        </Link>
        {article.published_at && (
          <time className="mt-8 block text-xs uppercase tracking-widest text-primary">
            {new Date(article.published_at).toLocaleDateString(lang === "it" ? "it-CH" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </time>
        )}
        <h1 className="mt-4 break-words font-serif text-4xl leading-tight md:text-5xl">{article.title}</h1>
        {article.excerpt && (
          <p className="mt-6 break-words text-lg leading-8 text-muted-foreground">{article.excerpt}</p>
        )}
        {article.cover_image_url && (
          <img src={article.cover_image_url} alt={article.title} className="mt-10 w-full rounded-lg" />
        )}
        <div className="prose prose-invert mt-10 max-w-none break-words text-base leading-8 text-foreground/90 prose-headings:font-serif prose-strong:font-bold prose-strong:text-foreground prose-a:text-primary prose-a:underline prose-a:underline-offset-4 hover:prose-a:opacity-80 prose-img:rounded-lg md:text-lg">
          {renderMarkdown(article.content)}
        </div>
        {related && related.length > 0 && (
          <aside className="mt-16 border-t border-border/60 pt-10">
            <h2 className="font-serif text-2xl">
              {lang === "it" ? "Articoli correlati" : "Related articles"}
            </h2>
            <div className="mt-8">
              <RelatedCarousel articles={related} lang={lang} />
            </div>
            <Link to="/news" className="mt-8 inline-block text-xs uppercase tracking-widest text-primary hover:underline">
              {lang === "it" ? "Tutti gli articoli" : "All articles"} →
            </Link>
          </aside>
        )}
      </article>
    </PageShell>
  );
}